import { Suspense, useCallback, useLayoutEffect, useRef, useState, type ComponentProps } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { AnimatePresence, motion, useIsPresent } from "motion/react";
import { useLocation, useOutlet } from "react-router-dom";
import { useMainScrollElement } from "@/app/context/MainScrollContext";
import { useRememberedScroll } from "@/features/preferences/page-state";
import { useUserSettingsStore } from "@/features/preferences/user-settings.store";
import { ChesslaLoadingMark } from "@/shared/components/ChesslaLoadingMark";
import {
  ROUTE_TRANSITION_MODE,
  routeAnnouncement,
  routeTransitionDirection,
  routeTransitionMotion,
  routeTransitionTitle,
  routeOwnsViewport,
} from "./content-route";

const RememberedRouteScroll = ({ pathname, ready }: { pathname: string; ready: boolean }) => {
  useRememberedScroll(pathname, useMainScrollElement(), ready);
  return null;
};

const PresentRoute = (props: ComponentProps<typeof motion.div>) => {
  const present = useIsPresent();
  return <motion.div {...props} inert={!present} aria-hidden={!present} />;
};

export const ContentRouteFrame = () => {
  const { pathname } = useLocation();
  const outlet = useOutlet();
  const theme = useTheme();
  const main = useMainScrollElement();
  const systemReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const animationsEnabled = useUserSettingsStore((state) => state.settings.animationsEnabled);
  const reducedMotion = systemReducedMotion || !animationsEnabled;
  const transitionRef = useRef<{ pathname: string; direction: 1 | -1 }>({ pathname, direction: 1 });
  const handledPath = useRef(pathname);
  const enteredPath = useRef<string | null>(null);
  const exitComplete = useRef(false);
  const scrollLock = useRef<{ overflowY: string; touchAction: string } | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const [lockedHeight, setLockedHeight] = useState<number | null>(null);

  if (transitionRef.current.pathname !== pathname)
    transitionRef.current = {
      pathname,
      direction: routeTransitionDirection(transitionRef.current.pathname, pathname),
    };

  const direction = transitionRef.current.direction;
  const ownsViewport = routeOwnsViewport(pathname);
  const motionPlan = routeTransitionMotion(reducedMotion, direction);
  const variants = {
    initial: motionPlan.initial,
    animate: motionPlan.animate,
    exit: (value: 1 | -1) => routeTransitionMotion(reducedMotion, value).exit,
  };
  const restoreScroll = useCallback(() => {
    if (main && scrollLock.current) {
      main.style.overflowY = scrollLock.current.overflowY;
      main.style.touchAction = scrollLock.current.touchAction;
    }
    scrollLock.current = null;
  }, [main]);
  const finishTransition = useCallback(
    (completedPath: string) => {
      if (
        completedPath !== transitionRef.current.pathname ||
        enteredPath.current !== completedPath ||
        !exitComplete.current
      )
        return;
      restoreScroll();
      setLockedHeight(null);
      setTransitioning(false);
    },
    [restoreScroll],
  );

  useLayoutEffect(() => {
    if (handledPath.current === pathname) {
      if (reducedMotion && transitioning) {
        restoreScroll();
        setLockedHeight(null);
        setTransitioning(false);
      }
      return;
    }
    handledPath.current = pathname;
    main?.scrollTo({ top: 0, left: 0, behavior: "auto" });
    if (main) main.scrollTop = 0;
    if (reducedMotion || !main) {
      restoreScroll();
      setLockedHeight(null);
      setTransitioning(false);
      return;
    }
    scrollLock.current ??= { overflowY: main.style.overflowY, touchAction: main.style.touchAction };
    enteredPath.current = null;
    exitComplete.current = false;
    setLockedHeight(main.clientHeight);
    setTransitioning(true);
    main.style.overflowY = "hidden";
    main.style.touchAction = "none";
  }, [main, pathname, reducedMotion, restoreScroll, transitioning]);

  useLayoutEffect(() => () => restoreScroll(), [restoreScroll]);

  return (
    <>
      <Box
        data-testid="route-transition-viewport"
        data-transitioning={transitioning}
        data-locked-height={lockedHeight ?? ""}
        sx={{
          position: "relative",
          isolation: "isolate",
          minWidth: 0,
          width: "100%",
          height: lockedHeight ? `${lockedHeight}px` : transitioning || ownsViewport ? "100%" : "auto",
          minHeight: "100%",
          maxHeight: transitioning ? (lockedHeight ? `${lockedHeight}px` : "100%") : "none",
          maxWidth: "100%",
          overflow: transitioning || pathname === "/games" ? "hidden" : "visible",
          bgcolor: "background.default",
          '&[data-transitioning="true"] > [data-testid="route-content-transition"]': {
            position: "absolute !important",
            inset: "0 !important",
            height: "100% !important",
            overflow: "hidden !important",
          },
        }}
      >
        {transitioning && !reducedMotion && (
          <Box
            data-testid="route-transition-title"
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 0,
              height: "100%",
              display: "grid",
              placeItems: "center",
              pointerEvents: "none",
              bgcolor: "background.default",
              color: "primary.main",
              borderBlock: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box sx={{ display: "grid", justifyItems: "center", gap: 1 }}>
              <Box sx={{ width: 42, height: 5, bgcolor: "secondary.main" }} />
              <Box
                sx={{
                  fontSize: { xs: "clamp(2.5rem,14vw,5rem)", md: "clamp(4rem,8vw,8rem)" },
                  fontWeight: 950,
                  letterSpacing: "-.065em",
                  lineHeight: 0.86,
                  textTransform: "uppercase",
                }}
              >
                {routeTransitionTitle(pathname)}
              </Box>
            </Box>
          </Box>
        )}
        <AnimatePresence
          initial={false}
          mode={ROUTE_TRANSITION_MODE}
          custom={direction}
          onExitComplete={() => {
            exitComplete.current = true;
            finishTransition(transitionRef.current.pathname);
          }}
        >
          <PresentRoute
            key={pathname}
            data-testid="route-content-transition"
            data-route-path={pathname}
            data-transition-direction={direction}
            variants={variants}
            initial="initial"
            animate="animate"
            exit="exit"
            onAnimationComplete={() => {
              if (pathname !== transitionRef.current.pathname) return;
              enteredPath.current = pathname;
              finishTransition(pathname);
            }}
            style={{
              position: "relative",
              zIndex: 1,
              width: "100%",
              height: transitioning || ownsViewport ? "100%" : "auto",
              minHeight: "100%",
              boxSizing: "border-box",
              // Keeps the last line of scrolling pages clear of rounded iPhone corners and the home indicator.
              paddingBottom: ownsViewport ? 0 : "calc(env(safe-area-inset-bottom, 0px) + 24px)",
              background: theme.palette.background.default,
            }}
          >
            <Suspense
              fallback={
                <ChesslaLoadingMark
                  fillContent
                  page={routeTransitionTitle(pathname)}
                  label={`Preparing ${routeTransitionTitle(pathname)}`}
                />
              }
            >
              {outlet}
              <RememberedRouteScroll pathname={pathname} ready={!transitioning} />
            </Suspense>
          </PresentRoute>
        </AnimatePresence>
      </Box>
      <Box
        role="status"
        aria-live="polite"
        sx={{
          clip: "rect(0 0 0 0)",
          clipPath: "inset(50%)",
          height: 1,
          left: 0,
          top: 0,
          overflow: "hidden",
          position: "absolute",
          whiteSpace: "nowrap",
          width: 1,
        }}
      >
        {routeAnnouncement(pathname)}
      </Box>
    </>
  );
};
